// providers/ExpandedItemProvider.tsx
import React, { type ReactNode, useState } from 'react';

import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { ExpandedItemProviderComponent, type ItemId } from './ExpandedItemContext';
import type { SchemaCreditRequestForm } from '../../schemas/creditRequestForm.schema';

export const ExpandedItemProvider = ({ children }: { children: ReactNode }) => {
    const [expandedItem, setExpandedItem] = useState<ItemId | null>(null);
    const {
        // eslint-disable-next-line unused-imports/no-unused-vars
        formState: { isDirty: formIsDirty },
        getFieldState,
    } = useFormContext<SchemaCreditRequestForm>();
    const { t } = useTranslation();

    const isExpanded = (id: ItemId) => expandedItem === id;

    const toggleItem = (id: ItemId) => {
        // If trying to close the current item
        if (expandedItem === id) {
            // Check if the field is dirty
            const { isDirty } = getFieldState(id);

            // Only close if not dirty
            if (!isDirty) {
                setExpandedItem(null);
            } else {
                // If dirty, show warning
                toast.warn(t('CREDIT_WORTHINESS.TOASTS.DIRTY'));
            }
        }
        // If opening a new item
        else {
            // Check if the currently expanded item is dirty
            if (expandedItem) {
                const { isDirty: currentIsDirty } = getFieldState(expandedItem);

                // If current item is dirty, don't change expanded item and show warning
                if (currentIsDirty) {
                    toast.warn(t('CREDIT_WORTHINESS.TOASTS.DIRTY'));
                    return;
                }
            }

            // If no dirty conflicts, set the new expanded item
            setExpandedItem(id);
        }
    };

    const isExpandedItemDirty = () => {
        if (expandedItem) {
            const { isDirty } = getFieldState(expandedItem);
            return isDirty;
        }
        return false;
    };

    return (
        <ExpandedItemProviderComponent
            value={{ expandedItem, isExpanded, isExpandedItemDirty, setExpandedItem, toggleItem }}
        >
            {children}
        </ExpandedItemProviderComponent>
    );
};
